import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * Line icons traced from `Sign Requests.dc.html`. The design draws each one as
 * an inline stroked SVG rather than a Material glyph, so the shapes live here
 * instead of pulling near-matches from `@mui/icons-material`.
 *
 * All of them inherit `currentColor`; size comes from the caller's `sx`.
 */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Folder used by the header action and the folder rows' icon tile. */
export const FolderGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.8}
      d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
    />
  </SvgIcon>
);

/** "Create Folder" — folder with a plus. */
export const FolderPlusGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
    />
    <path {...strokeProps} strokeWidth={2} d="M12 11v6M9 14h6" />
  </SvgIcon>
);

/** "Create Request" — plus in a circle. */
export const PlusCircleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2.2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2.2} d="M12 8v8M8 12h8" />
  </SvgIcon>
);

/** Bare plus — the narrow phone column's floating "Create" pill. */
export const PlusGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.3} d="M12 5v14M5 12h14" />
  </SvgIcon>
);

/** Document with a folded corner — the request rows' icon tile. */
export const DocumentGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.8}
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
    />
    <path {...strokeProps} strokeWidth={1.8} d="M14 3v5h5" />
  </SvgIcon>
);

export const SearchGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="11" cy="11" r="7" />
    <path {...strokeProps} strokeWidth={2} d="m21 21-4.3-4.3" />
  </SvgIcon>
);

export const CalendarGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="3"
      y="4"
      width="18"
      height="18"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M16 2v4M8 2v4M3 10h18" />
  </SvgIcon>
);

/**
 * Filter lines — the narrow phone column collapses the date-range chip to this
 * icon-only square, so the calendar glyph and its "All time" label give way to
 * a single filter mark.
 */
export const FilterLinesGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M3 5h18M6 12h12M10 19h4" />
  </SvgIcon>
);

export const CaretDownGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M6 9l6 6 6-6" />
  </SvgIcon>
);

/** Row chevron and breadcrumb separator. */
export const ChevronRightGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M9 6l6 6-6 6" />
  </SvgIcon>
);

export const HomeGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M3 10.5 12 3l9 7.5" />
    <path {...strokeProps} strokeWidth={2} d="M5 9.5V21h14V9.5" />
  </SvgIcon>
);

/** Vertical three-dot overflow trigger — filled, not stroked. */
export const KebabGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </SvgIcon>
);

/** Upload-into-tray mark used by the empty state. */
export const SignRequestGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.8}
      d="M4 14a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2"
    />
    <path {...strokeProps} strokeWidth={1.8} d="M12 22V8M8 12l4-4 4 4" />
  </SvgIcon>
);

/* ── Filters, views and tags (`Sign Requests - Filters & Tags.dc.html`) ──── */

/** Opposed up / down arrows — the Sort trigger. */
export const SortGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4"
    />
  </SvgIcon>
);

/** Bookmark — the Views trigger. */
export const BookmarkGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
    />
  </SvgIcon>
);

/** Tick marking the selected sort order or view. */
export const CheckGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.6} d="M5 12.5l4.5 4.5L19 7.5" />
  </SvgIcon>
);

/** Dismiss / remove cross. */
export const CloseGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.4} d="M18 6L6 18M6 6l12 12" />
  </SvgIcon>
);

/** Luggage tag — a row's tag trigger. */
export const TagGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"
    />
    <circle {...strokeProps} strokeWidth={2} cx="7.5" cy="7.5" r="1.5" />
  </SvgIcon>
);

/** Chain link — the "filters from a shared link" notice. */
export const LinkGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"
    />
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"
    />
  </SvgIcon>
);

/** Pencil — rename a view, edit a tag. */
export const PencilGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M12 20h9" />
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"
    />
  </SvgIcon>
);

export const TrashGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M3 6h18" />
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"
    />
  </SvgIcon>
);

/** Padlock — the read-only note in Manage tags. */
export const LockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="4"
      y="11"
      width="16"
      height="10"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M8 11V7a4 4 0 0 1 8 0v4" />
  </SvgIcon>
);

/** Circled "i" — the tag-limit note in the picker. */
export const InfoCircleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2} d="M12 8h.01M12 11v5" />
  </SvgIcon>
);

/* ── Filter categories (`Sign Requests - Filters & Tags v2.dc.html`) ─────── */

/** Clock — the Status filter. */
export const ClockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2} d="M12 7v5l3 2" />
  </SvgIcon>
);

/** Head and shoulders — the Sender filter. */
export const PersonGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="8" r="4" />
    <path {...strokeProps} strokeWidth={2} d="M4 21v-1a6 6 0 0 1 12 0v1" />
  </SvgIcon>
);

/** Panelled sheet — the Template filter. */
export const TemplateGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="3"
      y="4"
      width="18"
      height="16"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M3 9h18M9 9v11" />
  </SvgIcon>
);
